import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Typography,
    Box,
    Paper,
    Chip,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    PlayArrow,
    Pause,
    RestartAlt,
    KeyboardArrowDown,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    RotateRight,
} from '@mui/icons-material';
import ArcadeGameShell from '../ArcadeGameShell';
import { STACKFALL_DISPLAY_NAME } from '../gameNames';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { useGmSession } from '../useGmSession';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import { isGuestUser, useGameKeyGuard } from '../manualPlay';
import ResultView from '../ui/ResultView';
import UiButton from '../ui/Button';
import ManualSaveNotice from '../ManualSaveNotice';
import { useAuth } from '../../../Context/AuthContext';
import { ARCADE_THEME } from '../arcadeTheme';
import {
    createTetrisSession,
    deleteTetrisSession,
    moveTetrisSession,
    MAX_BATCH_ACTIONS,
    boardKindsToColors,
    pieceForPreview,
} from './tetrisGmClient';
import {
    TETRIS_BOARD_WIDTH,
    TETRIS_BOARD_HEIGHT,
    TETRIS_CANVAS_W,
    TETRIS_CANVAS_H,
    TETRIS_PREVIEW_COLS,
    TETRIS_PREVIEW_ROWS,
    TETRIS_PREVIEW_BLOCK,
    tetrisPlayfieldContentSx,
    tetrisGameRowSx,
    tetrisSideSlotSx,
    tetrisCanvasSlotSx,
    tetrisSideCardSx,
    tetrisNextPieceCardSx,
    tetrisCanvasOuterSx,
    tetrisCanvasInnerSx,
    tetrisCanvasStyle,
    arcadeSidePanelSx,
    drawTetrisBoard,
    drawTetrisPauseOverlay,
    drawNextPiecePreview,
} from '../visuals/TetrisScene';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';
import { createTickBatcher, startFixedClock } from '../tickBatcher';

const GAME_ID = 'tetris';
const { UPLOAD_URL } = ARCADE.tetris;
const ACCENT = GAME_ACCENT.tetris;
const THEME = ARCADE_THEME;
const BOARD_WIDTH = TETRIS_BOARD_WIDTH;
const BOARD_HEIGHT = TETRIS_BOARD_HEIGHT;
const CANVAS_WIDTH = TETRIS_CANVAS_W;
const CANVAS_HEIGHT = TETRIS_CANVAS_H;

const EFFECT_MS = 260;

export function prefersReducedMotion() {
    try {
        return typeof window !== 'undefined'
            && typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (_) {
        return false;
    }
}

/**
 * Derive purely visual effects from two consecutive GM boards:
 * - pulseCells: cells that became filled (piece locked), excluding the
 *   still-falling piece's cells;
 * - sweepRows: when `lines` grew, the bottom-most N rows of the stack get a
 *   sweep band (rows that dropped into place after the clear).
 */
export function computeBoardEffects(prevBoard, board, prevLines, lines, currentPiece) {
    if (!Array.isArray(prevBoard) || !Array.isArray(board)) return null;
    const own = new Set();
    if (currentPiece?.shape) {
        currentPiece.shape.forEach((row, dy) => row.forEach((v, dx) => {
            if (v) own.add(`${currentPiece.x + dx},${currentPiece.y + dy}`);
        }));
    }
    const cleared = Math.max(0, (lines || 0) - (prevLines || 0));
    const pulseCells = [];
    if (!cleared) {
        board.forEach((row, y) => (row || []).forEach((c, x) => {
            if (c && !prevBoard[y]?.[x] && !own.has(`${x},${y}`)) pulseCells.push([x, y]);
        }));
        // A big diff means a reset/new session, not a lock.
        if (pulseCells.length > 8) pulseCells.length = 0;
    }
    const sweepRows = [];
    for (let i = 0; i < Math.min(cleared, 4); i++) sweepRows.push(board.length - 1 - i);
    if (!pulseCells.length && !sweepRows.length) return null;
    return { pulseCells, sweepRows };
}

export function gravityMs(level) {
    return Math.max(120, 800 - (Math.max(1, level) - 1) * 60);
}

function NextPiecePreview({ piece }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        drawNextPiecePreview(ctx, piece);
    }, [piece]);

    return (
        <canvas
            ref={canvasRef}
            width={TETRIS_PREVIEW_COLS * TETRIS_PREVIEW_BLOCK}
            height={TETRIS_PREVIEW_ROWS * TETRIS_PREVIEW_BLOCK}
            aria-label={piece?.type ? `Next piece: ${piece.type}` : 'Next piece preview'}
            style={{
                display: 'block',
                border: `1px solid ${THEME.border}`,
                borderRadius: 6,
                backgroundColor: THEME.bg,
            }}
        />
    );
}

const emptyView = () => ({
    board: Array(BOARD_HEIGHT).fill(null).map(() => Array(BOARD_WIDTH).fill(0)),
    nextPiece: null,
    currentPiece: null,
    score: 0,
    lines: 0,
    level: 1,
    isGameOver: false,
    message: '',
});

const Tetris = () => {
    const canvasRef = useRef(null);
    const playPhaseRef = useRef('menu'); // menu | playing | paused | over
    const [playPhase, setPlayPhase] = useState('menu');
    // Result of a run ended with End Game (shown instead of the Start button).
    const [manualResult, setManualResult] = useState(null);
    const auth = useAuth();
    useGameKeyGuard(playPhase === 'playing' || playPhase === 'paused');
    const [view, setView] = useState(emptyView);
    const viewRef = useRef(view);
    const batcherRef = useRef(null);
    const softDropRef = useRef(false);

    const [showDialog, setShowDialog] = useState(false);
    const [dialogContent, setDialogContent] = useState({ title: '', message: '' });

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session });
    const gm = useGmSession({
        createSession: createTetrisSession,
        deleteSession: deleteTetrisSession,
    });

    useEffect(() => { viewRef.current = view; }, [view]);
    useEffect(() => { playPhaseRef.current = playPhase; }, [playPhase]);

    const applyGm = useCallback((data) => {
        const next = {
            board: boardKindsToColors(data.board),
            nextPiece: pieceForPreview(data.next_piece),
            // Display-only: used for the ghost outline when the GM reports it.
            currentPiece: pieceForPreview(data.current_piece),
            score: typeof data.score === 'number' ? data.score : 0,
            lines: typeof data.lines === 'number' ? data.lines : 0,
            level: typeof data.level === 'number' ? data.level : 1,
            isGameOver: Boolean(data.is_game_over || data.game_over),
            message: data.message || '',
        };
        viewRef.current = next;
        setView(next);
        return next;
    }, []);

    const fxRef = useRef(null);
    const fxRafRef = useRef(0);

    const paint = useCallback((board, paused) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        drawTetrisBoard(ctx, {
            board: board || viewRef.current.board,
            currentPiece: null,
            ghostPiece: paused ? null : viewRef.current.currentPiece,
            effects: paused ? null : fxRef.current,
        });
        if (paused) {
            drawTetrisPauseOverlay(ctx, CANVAS_WIDTH, CANVAS_HEIGHT, ACCENT);
        }
    }, []);

    // Visual-only lock pulse + line-clear sweep (skipped for reduced motion).
    const prevBoardRef = useRef(null);
    const prevLinesRef = useRef(0);
    useEffect(() => {
        const prevBoard = prevBoardRef.current;
        const prevLines = prevLinesRef.current;
        prevBoardRef.current = view.board;
        prevLinesRef.current = view.lines;
        const fx = computeBoardEffects(prevBoard, view.board, prevLines, view.lines, view.currentPiece);
        if (!fx || prefersReducedMotion()) {
            fxRef.current = null;
            return undefined;
        }
        const start = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const raf = typeof window !== 'undefined' && window.requestAnimationFrame
            ? window.requestAnimationFrame.bind(window) : null;
        if (!raf) return undefined;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / EFFECT_MS);
            fxRef.current = {
                pulse: fx.pulseCells.length ? { cells: fx.pulseCells, progress: t } : null,
                sweep: fx.sweepRows.length ? { rows: fx.sweepRows, progress: t } : null,
            };
            paint(viewRef.current.board, playPhaseRef.current === 'paused');
            if (t < 1) fxRafRef.current = raf(tick);
            else fxRef.current = null;
        };
        fxRafRef.current = raf(tick);
        return () => {
            if (typeof window !== 'undefined' && window.cancelAnimationFrame) {
                window.cancelAnimationFrame(fxRafRef.current);
            }
            fxRef.current = null;
        };
    }, [view.board, view.lines, view.currentPiece, paint]);

    useEffect(() => {
        paint(view.board, playPhase === 'paused');
    }, [view.board, view.currentPiece, playPhase, paint]);

    const finishOver = useCallback(async (next) => {
        setPlayPhase('over');
        playPhaseRef.current = 'over';
        setShowDialog(true);
        setDialogContent({
            title: 'Game Over',
            message: `Final Score: ${next.score}\nLines Cleared: ${next.lines}\nLevel: ${next.level}`,
        });
        recorder.finalizeAndUpload({
            score: next.score,
            extra: { lines: next.lines, level: next.level, rows: BOARD_HEIGHT, cols: BOARD_WIDTH },
        });
        await gm.clearSession();
    }, [gm, recorder]);

    const handleResult = useCallback(async (sid, data, actions) => {
        if (gm.sessionIdRef.current !== sid) return; // ended or restarted meanwhile
        const next = applyGm(data);
        const steps = Array.isArray(data.steps) && data.steps.length
            ? data.steps
            : actions.map((action) => ({
                action,
                board: data.board,
                score: next.score,
                lines: next.lines,
                level: next.level,
                current_piece: data.current_piece,
            }));
        steps.forEach((step) => recorder.recordTick({
            action: step.action,
            board: step.board,
            score: step.score,
            lines: step.lines,
            level: step.level,
            current_piece: step.current_piece,
        }));
        if (next.isGameOver) {
            if (batcherRef.current) batcherRef.current.stop();
            await finishOver(next);
        }
    }, [applyGm, finishOver, gm, recorder]);
    // The clock/batcher effect must not restart when these callbacks change.
    const handleResultRef = useRef(handleResult);
    useEffect(() => { handleResultRef.current = handleResult; }, [handleResult]);

    // Gravity ticks and key presses share one ordered queue; it is sent
    // whenever no request is in flight, so input is never dropped and the
    // drop pace does not depend on the round-trip time.
    const sendAction = useCallback((action) => {
        if (playPhaseRef.current !== 'playing') return;
        if (viewRef.current.isGameOver || !batcherRef.current) return;
        batcherRef.current.push(action);
    }, []);

    const startGame = useCallback(async () => {
        try {
            gm.setBusy(true);
            const data = await gm.createFreshSession();
            const next = applyGm(data);
            recorder.reset({
                init_state: {
                    board: data.locked_board || data.board,
                    rows: BOARD_HEIGHT,
                    cols: BOARD_WIDTH,
                },
                rows: BOARD_HEIGHT,
                cols: BOARD_WIDTH,
            });
            setShowDialog(false);
            setManualResult(null);
            setPlayPhase('playing');
            playPhaseRef.current = 'playing';
            paint(next.board, false);
        } catch (err) {
            console.error(err);
            setView((v) => ({ ...v, message: gmStartErrorMessage(err) }));
        } finally {
            gm.setBusy(false);
        }
    }, [applyGm, gm, paint, recorder]);

    const endToMenu = useCallback(async () => {
        await gm.clearSession();
        const next = emptyView();
        viewRef.current = next;
        setView(next);
        setPlayPhase('menu');
        playPhaseRef.current = 'menu';
        setShowDialog(false);
        paint(next.board, false);
    }, [gm, paint]);

    const togglePause = useCallback(() => {
        if (playPhaseRef.current === 'playing') {
            setPlayPhase('paused');
            playPhaseRef.current = 'paused';
        } else if (playPhaseRef.current === 'paused') {
            setPlayPhase('playing');
            playPhaseRef.current = 'playing';
        }
    }, []);

    // Gravity / soft-drop ticks on a fixed wall clock (level interval).
    useEffect(() => {
        if (playPhase !== 'playing') return undefined;
        const sid = gm.sessionIdRef.current;
        if (!sid) return undefined;
        const batcher = createTickBatcher({
            send: (actions) => moveTetrisSession(sid, actions),
            maxBatch: MAX_BATCH_ACTIONS,
            onResult: (data, actions) => { handleResultRef.current(sid, data, actions); },
            onError: (err) => console.warn('Tetris GM move failed', err),
            onDrop: (action, info) => console.warn('Tetris tick dropped', action, info),
        });
        batcherRef.current = batcher;
        const stopClock = startFixedClock({
            intervalMs: () => (softDropRef.current
                ? Math.min(80, gravityMs(viewRef.current.level) / 4)
                : gravityMs(viewRef.current.level)),
            onTick: () => sendAction(softDropRef.current ? 'down' : 'stay'),
        });
        return () => {
            stopClock();
            batcher.stop();
            if (batcherRef.current === batcher) batcherRef.current = null;
        };
    }, [playPhase, gm.sessionIdRef, sendAction]);

    // End Game: keep the run's result on screen instead of a bare menu.
    const endEarly = useCallback(async () => {
        const wasLive = playPhaseRef.current === 'playing' || playPhaseRef.current === 'paused';
        const last = viewRef.current;
        await endToMenu();
        if (wasLive) setManualResult({ score: last.score, lines: last.lines, level: last.level });
    }, [endToMenu]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                togglePause();
                return;
            }
            if (playPhaseRef.current !== 'playing') return;
            if (['ArrowLeft', 'a', 'A'].includes(e.key)) {
                e.preventDefault();
                sendAction('left');
            } else if (['ArrowRight', 'd', 'D'].includes(e.key)) {
                e.preventDefault();
                sendAction('right');
            } else if (['ArrowDown', 's', 'S'].includes(e.key)) {
                e.preventDefault();
                softDropRef.current = true;
            } else if (['ArrowUp', 'w', 'W', ' '].includes(e.key)) {
                e.preventDefault();
                sendAction('rotate');
            } else if (e.key === 'Enter') {
                e.preventDefault();
                sendAction('drop');
            }
        };
        const onKeyUp = (e) => {
            if (['ArrowDown', 's', 'S'].includes(e.key)) {
                softDropRef.current = false;
            }
        };
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
        };
    }, [sendAction, togglePause]);

    const panelSx = arcadeSidePanelSx();

    const { score, lines, level, nextPiece } = view;

    const statsCard = (
        <Paper sx={tetrisSideCardSx()}>
            <Typography variant="subtitle1" sx={{ color: 'white', mb: 2, fontWeight: 'bold', fontSize: '1.05rem' }}>
                Stats
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Chip
                    label={`Score: ${score}`}
                    size="medium"
                    variant="outlined"
                    sx={{ color: ACCENT, borderColor: ACCENT, justifyContent: 'flex-start' }}
                />
                <Chip
                    label={`Lines: ${lines}`}
                    size="medium"
                    variant="outlined"
                    sx={{ color: THEME.primary, borderColor: THEME.primary, justifyContent: 'flex-start' }}
                />
                <Chip
                    label={`Level: ${level}`}
                    size="medium"
                    variant="outlined"
                    sx={{ color: THEME.gold, borderColor: THEME.gold, justifyContent: 'flex-start' }}
                />
            </Box>
            <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ color: THEME.secondary, mb: 0.75, display: 'block' }}>
                    Next level at {(Math.floor(lines / 10) + 1) * 10} lines
                </Typography>
                <LinearProgress
                    variant="determinate"
                    value={(lines % 10) * 10}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: THEME.grid,
                        '& .MuiLinearProgress-bar': { backgroundColor: ACCENT },
                    }}
                />
            </Box>
        </Paper>
    );

    const nextPieceCard = (
        <Paper sx={tetrisNextPieceCardSx()}>
            <Typography variant="subtitle1" sx={{ color: 'white', mb: 2, fontWeight: 'bold', fontSize: '1.05rem' }}>
                Next Piece
            </Typography>
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
            }}
            >
                <NextPiecePreview piece={nextPiece} />
            </Box>
        </Paper>
    );

    const canvasFrame = (
        <Box sx={tetrisCanvasOuterSx(ACCENT)}>
            <Box sx={tetrisCanvasInnerSx(ACCENT)}>
                <canvas
                    ref={canvasRef}
                    width={CANVAS_WIDTH}
                    height={CANVAS_HEIGHT}
                    style={tetrisCanvasStyle()}
                />
            </Box>
        </Box>
    );

    return (
        <ArcadeGameShell
            title={STACKFALL_DISPLAY_NAME}
            subtitle="Stack falling blocks and clear lines — play manually or upload an agent"
            accentColor={ACCENT}
            boilerplate={GAME_BOILERPLATE.tetris}
            session={session}
            growPlayfield
            playfieldDark
            shellLayoutOverrides={{
                outerPx: { xs: 4, sm: 6, lg: 8 },
                gridGap: { xs: 2.5, lg: 3 },
                playColumnPadding: 0,
                gridColumns: {
                    xs: '1fr',
                    lg: 'minmax(200px, 240px) minmax(0, 1fr) minmax(264px, 336px)',
                },
            }}
            belowAgent={(
                <Paper sx={panelSx}>
                    <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
                        Controls
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <KeyboardArrowLeft sx={{ color: ACCENT, fontSize: 18 }} />
                            <Typography sx={{ color: THEME.secondary, fontSize: '0.9rem' }}>
                                A / ← — Move Left
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <KeyboardArrowRight sx={{ color: ACCENT, fontSize: 18 }} />
                            <Typography sx={{ color: THEME.secondary, fontSize: '0.9rem' }}>
                                D / → — Move Right
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <KeyboardArrowDown sx={{ color: ACCENT, fontSize: 18 }} />
                            <Typography sx={{ color: THEME.secondary, fontSize: '0.9rem' }}>
                                S / ↓ — Soft Drop
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RotateRight sx={{ color: ACCENT, fontSize: 18 }} />
                            <Typography sx={{ color: THEME.secondary, fontSize: '0.9rem' }}>
                                W / ↑ / Space — Rotate
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            )}
        >
            <>
                <Box sx={tetrisPlayfieldContentSx()}>
                    {playPhase === 'menu' && manualResult ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                            <ResultView
                                title="Run ended"
                                score={manualResult.score}
                                details={(
                                    <Typography variant="body2" sx={{ color: THEME.secondary }}>
                                        Lines {manualResult.lines} · Level {manualResult.level}
                                    </Typography>
                                )}
                                endedManually
                                isGuest={isGuestUser(auth)}
                                accentColor={ACCENT}
                                onPlayAgain={startGame}
                                autoFocus={false}
                            />
                        </Box>
                    ) : playPhase === 'menu' ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                            <UiButton
                                variant="primary"
                                startIcon={<PlayArrow />}
                                onClick={startGame}
                                disabled={gm.busy}
                            >
                                Start Game
                            </UiButton>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
                            <UiButton
                                variant="primary"
                                startIcon={playPhase === 'playing' ? <Pause /> : <PlayArrow />}
                                onClick={togglePause}
                                disabled={playPhase === 'over'}
                            >
                                {playPhase === 'playing' ? 'Pause' : 'Resume'}
                            </UiButton>
                            <UiButton
                                variant="danger"
                                startIcon={<RestartAlt />}
                                onClick={endEarly}
                            >
                                End Game
                            </UiButton>
                        </Box>
                    )}

                    {view.message && playPhase === 'menu' && (
                        <Typography sx={{ color: THEME.secondary, textAlign: 'center', mt: 1 }}>
                            {view.message}
                        </Typography>
                    )}

                    <Box sx={tetrisGameRowSx()}>
                        <Box sx={tetrisSideSlotSx('left')}>{statsCard}</Box>
                        <Box sx={tetrisCanvasSlotSx()}>{canvasFrame}</Box>
                        <Box sx={tetrisSideSlotSx('right')}>{nextPieceCard}</Box>
                    </Box>
                </Box>

                <Dialog
                    open={showDialog}
                    onClose={() => setShowDialog(false)}
                    PaperProps={{
                        sx: {
                            backgroundColor: THEME.cardBg,
                            backdropFilter: 'blur(10px)',
                            border: `1px solid ${THEME.border}`,
                            borderRadius: 3,
                        },
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center', color: 'white' }}>
                        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                            {dialogContent.title}
                        </Typography>
                    </DialogTitle>
                    <DialogContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ color: THEME.secondary, whiteSpace: 'pre-line' }}>
                            {dialogContent.message}
                        </Typography>
                        <ManualSaveNotice notice={recorder.saveNotice} color={COLOR.text} />
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                        <UiButton
                            onClick={() => {
                                setShowDialog(false);
                                startGame();
                            }}
                            variant="primary"
                        >
                            Play Again
                        </UiButton>
                        <UiButton
                            onClick={endToMenu}
                            variant="secondary"
                        >
                            Main Menu
                        </UiButton>
                    </DialogActions>
                </Dialog>
            </>
        </ArcadeGameShell>
    );
};

export default Tetris;

export {
    TETRIS_BOARD_WIDTH,
    TETRIS_BOARD_HEIGHT,
    TETRIS_CELL_WIDTH,
    TETRIS_CELL_HEIGHT,
    TETRIS_CANVAS_W,
    TETRIS_CANVAS_H,
    drawNextPiecePreview,
} from '../visuals/TetrisScene';
